import apiClient from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import type { CheckboxSection } from '../types/api'
import type { ApplicationDetailData, DenialReasonsResponse } from '../types/application'
import { throwServiceError } from './serviceHelpers'

/** Application detail by applicant ID. */
export async function getApplicationDetail(
  applicantId: string | number,
): Promise<ApplicationDetailData> {
  try {
    const url = API_ENDPOINTS.APPLICATION_DETAIL(applicantId)
    const separator = url.includes('?') ? '&' : '?'
    const response = await apiClient.get<ApplicationDetailData>(
      `${url}${separator}_t=${Date.now()}`,
    )
    return response.data
  } catch (error) {
    throwServiceError(error, 'Failed to fetch application details.')
  }
}

/** Denial reasons for applicant. */
export async function getDenialReasons(applicantId: number): Promise<DenialReasonsResponse> {
  try {
    const response = await apiClient.get<DenialReasonsResponse>(
      API_ENDPOINTS.APPLICATION_DENIAL_REASONS(applicantId),
    )

    return response.data
  } catch (error) {
    throwServiceError(error, 'Error fetching denial reasons.')
  }
}

export function filterLockedDenialCheckboxContent(
  response: DenialReasonsResponse,
): DenialReasonsResponse {
  if (!response.checkbox_content) {
    return response
  }

  const filteredSections: Record<string, unknown> = {}

  for (const [sectionKey, sectionRaw] of Object.entries(response.checkbox_content)) {
    const section = sectionRaw as CheckboxSection
    const filteredChildren =
      section.child && Array.isArray(section.child)
        ? section.child.filter((item) => item.isLock === 0)
        : []

    if (filteredChildren.length > 0) {
      filteredSections[sectionKey] = {
        ...section,
        child: filteredChildren,
      }
    }
  }

  return {
    ...response,
    checkbox_content: filteredSections as DenialReasonsResponse['checkbox_content'],
  }
}

/** Translate application to English. */
export async function translateApplication(applicantId: string | number): Promise<void> {
  try {
    await apiClient.post(`/api/translate_application/${applicantId}`, {})
  } catch (error) {
    throwServiceError(error, 'Failed to translate application.')
  }
}

/** Translate single field — POST /api/translate; returns original text on failure (Angular translateContent). */
export async function translateField(field: string, value: string): Promise<string> {
  try {
    const response = await apiClient.post<Record<string, string>>('/api/translate', {
      [field]: value,
    })
    const translated = response.data?.[field]
    return typeof translated === 'string' && translated ? translated : value
  } catch {
    return value
  }
}
